/**
 * In-band ID3 control-plane cue handling.
 *
 * MediaLive injects operator metadata into the live transport stream as ID3 TXXX frames
 * (see monk-environment/docs/id3-control-plane-brief.md):
 *  - {mode: "state", epoch, ...}  — stamped on every segment; authoritative current state.
 *  - {mode: "event", type, event_id, payload, sent_at} — one-shot cues (banner show/hide, prompts).
 *
 * video.js/VHS surfaces these as cues on a "metadata" TextTrack. This module extracts and parses
 * them and hands structured payloads to the caller. It must only ever be attached to a LIVE
 * player: harvested VOD replays the original ID3, and a VOD viewer must never see old prompts.
 */

const MAX_EVENT_AGE_MS = 5 * 60 * 1000;

/**
 * Parse one TextTrack cue into a control-plane payload, or null when it isn't one.
 * VHS exposes ID3 TXXX frames as cue.value = {key: 'TXXX', data: <string>, ...}.
 */
export function parseId3Cue(cue) {
    const value = cue && cue.value;
    if (!value) {
        return null;
    }

    const raw = typeof value.data === 'string' ? value.data : value.info;
    if (typeof raw !== 'string') {
        return null;
    }

    // Payloads are JSON objects carrying a "mode" discriminator; ignore everything else
    // (timecode PRIV frames, other tooling's tags).
    const start = raw.indexOf('{');
    if (start === -1) {
        return null;
    }

    let payload;
    try {
        payload = JSON.parse(raw.slice(start));
    } catch (e) {
        return null;
    }

    if (!payload || (payload.mode !== 'state' && payload.mode !== 'event')) {
        return null;
    }

    return payload;
}

/**
 * Whether a one-shot event is too old to honor. Guards against replayed cues (a seek back
 * within the live window, or ID3 that survived into a recording).
 */
export function isStaleEvent(payload, now = Date.now()) {
    if (!payload.sent_at) {
        return false;
    }

    const sentAt = Date.parse(payload.sent_at);

    return !isNaN(sentAt) && now - sentAt > MAX_EVENT_AGE_MS;
}

/**
 * Attach the cue reader to a video.js player.
 *
 * handlers.onState(payload)  — called for each (deduped) state tag.
 * handlers.onEvent(payload)  — called once per event_id, stale events dropped.
 *
 * Returns a detach function.
 */
export function attachId3CueReader(player, handlers) {
    const seenEventIds = new Set();
    let lastStateJson = null;
    const trackListeners = [];

    const handlePayload = (payload) => {
        if (payload.mode === 'state') {
            // The tag repeats on every segment; only surface actual changes.
            const json = JSON.stringify(payload);
            if (json === lastStateJson) {
                return;
            }
            lastStateJson = json;

            if (handlers.onState) {
                handlers.onState(payload);
            }

            return;
        }

        if (!payload.event_id || seenEventIds.has(payload.event_id) || isStaleEvent(payload)) {
            return;
        }
        seenEventIds.add(payload.event_id);

        if (handlers.onEvent) {
            handlers.onEvent(payload);
        }
    };

    const watchTrack = (track) => {
        if (track.kind !== 'metadata') {
            return;
        }

        // Cues only fire on tracks that are at least hidden (disabled tracks are inert).
        if (track.mode === 'disabled') {
            track.mode = 'hidden';
        }

        const onCueChange = () => {
            const cues = track.activeCues || [];
            for (let i = 0; i < cues.length; i++) {
                const payload = parseId3Cue(cues[i]);
                if (payload) {
                    handlePayload(payload);
                }
            }
        };

        track.addEventListener('cuechange', onCueChange);
        trackListeners.push({ track, onCueChange });
    };

    const trackList = player.textTracks();
    for (let i = 0; i < trackList.length; i++) {
        watchTrack(trackList[i]);
    }

    const onAddTrack = (event) => watchTrack(event.track);
    trackList.addEventListener('addtrack', onAddTrack);

    return function detach() {
        trackList.removeEventListener('addtrack', onAddTrack);
        trackListeners.forEach(({ track, onCueChange }) => {
            track.removeEventListener('cuechange', onCueChange);
        });
        trackListeners.length = 0;
    };
}
